import React from "react";
import CurrencyInput from "@/components/CurrencyInput";

export const STEPS = [
  "Agent Details", "Lead Passenger", "Destination", "Covering Cities",
  "Tour Dates", "Flights", "Hotels", "Day-wise Itinerary",
  "Inclusions", "Exclusions", "Policies", "Pricing",
];

export const emptyQuote = {
  package_title: "",
  customer_id: "", customer: null,
  lead: { name: "", mobile: "", email: "", adults: 2, children: 0, child_ages: [] },
  destination: { country: "", country_code: "", flag_url: "", hero_image: "",
                 overview: "", best_time: "", currency: "", weather: "",
                 attractions: "", experiences: "" },
  destinations: [],
  cities: [],
  start_date: "", end_date: "", total_nights: 0, total_days: 0,
  flights: [], hotels: [], inclusions: [], exclusions: [], itinerary: [],
  policies: { payment_policy: "", cancellation_policy: "", terms_conditions: "" },
  pricing: { currency: "INR", package_cost: 0, per_person_cost: 0, taxes: 0,
             tcs: 0, visa_charges: 0, discount: 0, markup: 0, service_charges: 0,
             grand_total: 0 },
  status: "draft",
  template_id: "classic",
  auto_hotel_dates: true,
};

// ---------- Timezone-safe date helpers (used by hotel auto-calc) ----------
export const addDays = (isoDate, days) => {
  if (!isoDate) return "";
  const [y, m, d] = isoDate.slice(0, 10).split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + (days || 0));
  return dt.toISOString().slice(0, 10);
};

export const buildCityDatesMap = (startDate, cities) => {
  const map = {};
  if (!startDate) return map;
  let cursor = startDate.slice(0, 10);
  for (const c of cities || []) {
    if (!c || !c.city) continue;
    const nights = Number(c.nights) || 0;
    const check_in = cursor;
    const check_out = addDays(cursor, nights);
    if (!map[c.city]) map[c.city] = { check_in, check_out, nights };
    cursor = check_out;
  }
  return map;
};

export const matchHotelToCity = (hotel, cityMap) => {
  const hay = `${hotel?.address || ""} ${hotel?.name || ""} ${hotel?.city || ""}`.toLowerCase();
  for (const [city, info] of Object.entries(cityMap)) {
    if (city && hay.includes(city.toLowerCase())) return info;
  }
  return null;
};

/* ---------- Shared tiny inputs ---------- */
export function F({ label, value, onChange, type="text", testid, disabled }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-widest text-slate-500 mb-1.5">{label}</label>
      <input data-testid={testid} type={type} value={value ?? ""} onChange={(e)=>onChange(e.target.value)} disabled={disabled} className={`w-full border border-border rounded px-3 py-2 text-sm ${disabled ? "bg-slate-100 text-slate-500 cursor-not-allowed" : ""}`} />
    </div>
  );
}

export function TxtArea({ label, value, onChange, testid, rows=2 }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-widest text-slate-500 mb-1.5">{label}</label>
      <textarea data-testid={testid} rows={rows} value={value || ""} onChange={(e)=>onChange(e.target.value)} className="w-full border border-border rounded px-3 py-2 text-sm" />
    </div>
  );
}

export const Money = ({ label, testid, value, onChange, currency = "₹" }) => (
  <div>
    <label className="block text-xs uppercase tracking-widest text-slate-500 mb-1.5">{label}</label>
    <CurrencyInput testid={testid} value={value} onChange={onChange} prefix={currency} />
  </div>
);

/* ---------- Smart Inclusions / Exclusions engines ---------- */
export const MEAL_PLAN_LABEL = {
  CP: "Daily breakfast",
  EP: "",
  MAP: "Daily breakfast and dinner",
  AP: "All meals (breakfast, lunch and dinner)",
  BB: "Daily breakfast",
  HB: "Half-board (breakfast and dinner)",
  FB: "Full-board (breakfast, lunch and dinner)",
};

export function _titleCase(s) {
  return (s || "").toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase()).trim();
}

export function generateInclusions(q) {
  const out = [];
  const push = (s) => { const t = (s || "").trim(); if (t) out.push(t); };

  const hotels = (q.hotels || []).filter((h) => h && (h.name || h.city || h.nights));
  hotels.forEach((h) => {
    const n = parseInt(h.nights) || 0;
    const city = (h.city || "").trim();
    const name = (h.name || "").trim();
    if (!city && !name) return;
    const nightLabel = n > 0 ? `${n} night${n > 1 ? "s" : ""}` : "Accommodation";
    const nightsPart = n > 0 ? `${nightLabel} accommodation` : "Accommodation";
    if (city && name) push(`${nightsPart} in ${city} at ${name}`);
    else if (city) push(`${nightsPart} in ${city}`);
    else push(`${nightsPart} at ${name}`);
  });

  hotels.forEach((h) => {
    const key = (h.meal_plan || "").toUpperCase().trim();
    const label = MEAL_PLAN_LABEL[key];
    if (!label) return;
    const where = (h.name || "").trim() || (h.city || "").trim();
    push(where ? `${label} at ${where}` : label);
  });

  const flights = (q.flights || []).filter((f) => f && (f.airline || f.flight_number || f.dep_airport));
  if (flights.length) {
    push("Flight tickets as per itinerary");
    const bags = [...new Set(flights.map((f) => (f.baggage || "").trim()).filter(Boolean))];
    if (bags.length) push(`Checked baggage allowance: ${bags.join(", ")}`);
  }

  const itin = q.itinerary || [];
  const sightsByCity = {};
  const seenPerCity = {};
  const seenActs = new Set();
  let hasAirportTransfer = false;
  let hasRailTransfer = false;
  let hasBusTransfer = false;
  let hasGenericTransfer = false;

  itin.forEach((d) => {
    const blob = `${d.title || ""} ${d.description || ""} ${d.sightseeing || ""} ${d.notes || ""}`.toLowerCase();
    if (/\bairport\b/.test(blob) && /(transfer|pick[- ]?up|drop)/.test(blob)) hasAirportTransfer = true;
    if (/(railway|train station)/.test(blob) && /(transfer|pick[- ]?up|drop)/.test(blob)) hasRailTransfer = true;
    if (/(bus stand|bus station)/.test(blob) && /(transfer|pick[- ]?up|drop)/.test(blob)) hasBusTransfer = true;
    if (/\btransfer(s)?\b/.test(blob) && !hasAirportTransfer && !hasRailTransfer && !hasBusTransfer) hasGenericTransfer = true;

    const cityKey = (d.city || "").trim() || "Other";
    (d.sightseeing || "").split(/[,\n;]/).forEach((s) => {
      const t = s.trim();
      if (!t || t.length <= 2 || t.length >= 80) return;
      const name = _titleCase(t);
      sightsByCity[cityKey] = sightsByCity[cityKey] || [];
      seenPerCity[cityKey] = seenPerCity[cityKey] || new Set();
      const k = name.toLowerCase();
      if (!seenPerCity[cityKey].has(k)) {
        seenPerCity[cityKey].add(k);
        sightsByCity[cityKey].push(name);
      }
    });
    const title = (d.title || "").trim();
    if (/\b(excursion|activity|activities|cruise|safari|tour)\b/i.test(title) && title.length < 80) {
      seenActs.add(title);
    }
  });

  if (hasAirportTransfer) push("Airport transfers (pick-up and drop)");
  if (hasRailTransfer) push("Railway station transfers");
  if (hasBusTransfer) push("Bus stand transfers");
  if (hasGenericTransfer && !hasAirportTransfer && !hasRailTransfer && !hasBusTransfer) {
    push("Private transfers as per itinerary");
  }
  Object.entries(sightsByCity).forEach(([city, items]) => {
    if (!items.length) return;
    const label = city && city !== "Other" ? `Sightseeing in ${city}` : "Sightseeing";
    push(`${label}: ${items.join(", ")}`);
  });
  [...seenActs].slice(0, 6).forEach((a) => push(a));

  if (hotels.length || itin.length) push("All applicable services specifically mentioned in the itinerary");
  if (hotels.length) push("Applicable hotel taxes");
  if (hotels.length || itin.length || flights.length) push("Assistance during the tour");

  const seen = new Set();
  return out.filter((s) => {
    const k = s.toLowerCase();
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
}

export const DEFAULT_EXCLUSIONS = [
  { text: "Personal expenses (laundry, phone calls, mini-bar, etc.)", conflicts: [] },
  { text: "Travel insurance", conflicts: ["insurance"] },
  { text: "Visa fees and charges", conflicts: ["visa"] },
  { text: "Tips and gratuities", conflicts: ["tips", "gratuit"] },
  { text: "Meals not specifically mentioned in the inclusions", conflicts: ["all meals", "full-board", "full board"] },
  { text: "Additional sightseeing or activities not mentioned in the itinerary", conflicts: [] },
  { text: "Monument / attraction entry fees unless specifically included", conflicts: ["entry fee", "entrance fee", "monument"] },
  { text: "Camera and video charges at monuments / attractions", conflicts: ["camera"] },
  { text: "Early check-in and late check-out charges", conflicts: ["early check-in", "late check-out"] },
  { text: "Any services not specifically mentioned under inclusions", conflicts: [] },
  { text: "Expenses caused by delays, cancellations or circumstances beyond our control", conflicts: [] },
  { text: "Any increase in taxes, government charges or other applicable charges", conflicts: [] },
];

export function generateExclusions(q) {
  const incBlob = (q.inclusions || []).join(" | ").toLowerCase();
  const out = [];
  DEFAULT_EXCLUSIONS.forEach(({ text, conflicts }) => {
    const clash = conflicts.some((c) => incBlob.includes(c));
    if (!clash) out.push(text);
  });
  const seen = new Set();
  return out.filter((s) => {
    const k = s.toLowerCase();
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
}
